import {
  DashboardType,
  HistoryDateRequest,
  Observation,
  StationTypes
} from '../Requests/StationRequests'
import type { CheckMethod } from '@/constants/ds-attendance.constants'
import { DoctorStationStatus } from '@/translations/doctor.station.status'
import type { RequiredCheckPolicyRef, RequiredCheckSource } from '@/utils/registrationCheck'
import { HealthUnitModel } from '@/types/health.unit'
import type { PermanenceInfo } from '@/types/permanence.types'
import type { PreAllocationTag } from '@/types/qualification-eligibility.types'
import type { StationAcceptanceTerm, StationAllocationInvite } from './AllocationInviteResponses'
import { UserInfo } from './UserResponse'
import type {
  CoverageBlockedReason,
  CoverageCounts,
  CoverageFields,
  CoverageStatus
} from './CoverageResponses'

export type CellTypes = 'FIXO' | 'ABERTO' | 'COBERTURA' | 'EXTRA'

export type CompanyWeekList = {
  companyId: number
  companyName: string
  healthUnities: HealthUnitWeekList[]
}

export type HealthUnitWeekList = {
  healthUnitId: number
  healthUnitName: string
  healthUnitIsActive: boolean
  healthUnitLatitude: string
  healthUnitLongitude: string
  schedules: ScheduleWeekList[]
}

export type PositionWeekList = {
  title: string
  positionId: number
  stations: StationWeekList[][]
}

export type ShiftHoursList = {
  title: string // ex: "07:00-19:00"
  positions: PositionWeekList[]
}

export type ShiftList = {
  title: string
  shiftId: number
  hours: ShiftHoursList[]
}

export type ScheduleWeekList = {
  scheduleId: number
  scheduleName: string
  scheduleTimezone: string
  shifts: ShiftList[]
}

export type TableHead = {
  label: string
  value: string
}

export type PostStationCellStatus = 'APPROVED' | 'REVIEW'

export type StationSectorRef = {
  id: number
  name: string
  /** Contrato A9. Com valor, a UI mostra o sufixo "(arquivado)" (spec §7). */
  archivedAt?: string | null
  /** O plano do BE (mapper `toStationSectorView`) expõe `archived: boolean`; aceito até o BE fechar em `archivedAt`. */
  archived?: boolean
}

export type SpecialtySource = 'official' | 'company'

export type StationSpecialtyRef = {
  id: number
  name: string
  source: SpecialtySource
  archivedAt?: string | null
  /** Ver `StationSectorRef.archived`. */
  archived?: boolean
}

export type StationWeekList = CoverageFields & {
  date: Date
  startHour: Date
  endHour: Date
  timezone: string
  stationType: StationTypes
  doctorStationId: number
  doctorStationUserSponsor: UserInfo
  scheduleId: number
  doctorId: number
  stationId: number
  opened: boolean
  ourTeam: boolean
  value: number
  inCash: boolean
  cell: CellTypes
  doctorName?: string
  doctorAvatar?: string | null
  crm?: string
  type?: {
    success?: boolean
    error?: boolean
    waiting?: boolean
    pendences?: boolean
  }
  checks: HistoryDateRequest[]
  hasObservations: boolean
  advanceRequested: boolean
  advancePaid: boolean
  doctorPending?: boolean
  doctorConfirmed?: boolean
  urlConviteDoctor?: string
  modifiedValue?: boolean
  stationValue: number
  status: DoctorStationStatus
  doctorStationReminderStatus?: DoctorStationReminderStatus
  isAlert?: boolean
  position: Position
  healthUnit: HealthUnitModel
  requiredCheckNotOurTeam?: boolean
  requiredCheckOurTeam?: boolean
  exceptionSummaryStatus?: ExceptionSummaryStatus | null
  postStationStatus?: PostStationCellStatus | null
  doctorNpsScore?: number | null
  /** Opcionais: BE anterior à F3 não manda as chaves; a grade trata como "sem setor/especialidade". */
  sector?: StationSectorRef | null
  specialty?: StationSpecialtyRef | null
  /**
   * Aditivo (SPEC §5.1): plantão pré-alocado conta como coberto, com tag. Ausente com a flag
   * do backend desligada; `null` = sem pré-alocação.
   */
  preAllocation?: PreAllocationTag | null
  /**
   * Aditivo (acceptance-terms, backend #3572): convite pendente do plantão. Ausente em backend
   * anterior; `null` sem convite ou com a flag do backend desligada.
   */
  allocationInvite?: StationAllocationInvite | null
}

export type ListStationsResponse = {
  count: number
  type: DashboardType
  data: CompanyWeekList[]
  tableHead: TableHead[]
  /** Contagem por status com todos os filtros EXCETO `coverageStatus` (chips do M10-A). */
  coverageCounts?: CoverageCounts
  /** Total de escalas do filtro (Mês paginado por escala). Ausente em BE antigo. */
  totalSchedules?: number
}

export type RowConsultingResponse = CoverageFields & {
  id: number
  name: string
  crm: string
  /**
   * Valor do plantão. UNIDENTIFIED: o BE mantém `value`/`modifiedValue` PREENCHIDOS; o front
   * mostra "—" na coluna "Valor" (fora dos totais) e exibe o valor em "Valor ref.".
   */
  value: string | null
  /**
   * Valor de referência do plantão ocupado sem identificação (informativo, não é pago).
   * DOCTOR → `null`; ausente em BE antigo. "Valor ref." usa `referenceValue ?? modifiedValue ?? value`.
   */
  referenceValue?: number | null
  modifiedValue: string | null
  paymentValue: string | null
  schedule: string
  timezone: string
  date: string
  startHour: string
  endHour: string
  check: string
  checkin: string
  checkout: string
  ourTeam: string
  inCash: boolean
  stationType: string
  hours: string
  observations: string
  hoursWorked: string
  advanceRequested: string
  advancePaid: string
  status: string
  /**
   * Contrato A9: id do PLANTÃO (o `id` da linha é o da alocação `doctorStation`).
   * Alimenta o lote de setor/especialidade. Opcional até a F3 do BE.
   */
  stationId?: number
  healthUnitId?: number
  sectorId?: number | null
  sectorName?: string | null
  specialtyId?: number | null
  specialtySource?: SpecialtySource | null
  specialtyName?: string | null
  /** Forma aninhada do plano do BE (Task "consulting"); o web prefere os campos planos acima. */
  sector?: StationSectorRef | null
  specialty?: StationSpecialtyRef | null
  checkinData?: {
    url: string
    distance: string
  }
  checkoutData?: {
    url: string
    distance: number
  }
}
export type ConsultingResponse = {
  totalRecords: number
  rows: RowConsultingResponse[]
  coverageCounts?: CoverageCounts
}

export type StationsToApproveResponse = {
  stationId: number
  doctorStationId: number
  doctorName: string
  doctorCRM: string
  healthUnitName: string
  date: string
  hoursStation: string
  hoursCheck: string
  value: string
  observations: string
  valueProposed: string
  payment: string
}

export type StationsToApproveListResponse = {
  totalRecords: number
  rows: StationsToApproveResponse[]
}

// Station Details Types

interface BaseEntity {
  id: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

interface CRM extends BaseEntity {
  name: string
  number: string
  uf: string
  type: string
  status: string
}

interface Doctor extends BaseEntity {
  user: UserInfo
  mainCrm: CRM
}

interface Company extends BaseEntity {
  avatarUrl: string | null
  name: string
  email: string
  cnpj: string
  tradingName: string | null
  foundingDate: string | null
  qtyEmployees: number | null
  description: string | null
}

interface HealthUnit extends BaseEntity {
  name: string
  email: string
  cnpj: string
  profile: string | null
  linkWaze: string | null
  quantityColaborators: number | null
  generalManager: string | null
  technicalManager: string | null
  avatarUrl: string | null
  reachRadius: number | null
  company: Company
}

interface Schedule extends BaseEntity {
  name: string
  timezone: string
  endDate: string | null
  requiresDoctorPaymentFormation: boolean
  proportionalityRule: string
  requiredCheckNotOurTeam: boolean
  requiredCheckOurTeam: boolean
  doctorHandoverAuthorizationMinutes: number
  healthUnit?: HealthUnit
  paymentPolicies: any[]
  operationalGroup: any | null
  allowDoctorStationHandover: boolean
  teamDoctorReminderEnabled: boolean
  externalDoctorReminderEnabled: boolean
  /** Presença do campo = backend com a feature; `true` = a escala permite marcar. */
  allowUnregisteredDoctor?: boolean
  /** Kill switch global (FF_UNIDENTIFIED_COVERAGE_ENABLED); ausente = tratado como ligado. */
  unidentifiedCoverageFeatureEnabled?: boolean
  /** Médicos "coringa" recusados nesta escala; ausente = BE antigo (só vale o 422). */
  blockedPlaceholderDoctorIds?: number[]
}

interface Position extends BaseEntity {
  name: string
}

export enum ExceptionSummaryStatus {
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  IN_PROGRESS = 'IN_PROGRESS'
}

export interface CheckEvidence {
  id: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  name: string
  path: string
  eventType: string | null
}

export interface DoctorCheck {
  id: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  checkin: string // Data ISO (ex: "2025-10-29T16:45:00.000Z")
  checkout: string // Data ISO
  isMain: boolean
  origin: 'WEB' | 'DOCTOR' | 'FOLHA' | 'EXTERNAL_APP'
  checkinLatitude: string | number | null
  checkinLongitude: string | number | null
  checkoutLatitude: string | number | null
  checkoutLongitude: string | number | null
  checkMethod: 'GEOLOCATION' | 'MANUAL' | 'BIOMETRIC' | null
  checkEvidences: CheckEvidence[]
  permanence?: PermanenceInfo | null
  /**
   * F2 — campo ADITIVO de `GET /stations/:id` (B17 §1.2): o container de Registro de
   * Atividade que originou este check, ou `null`. Opcional porque backend anterior à F2
   * não o devolve; sem ele o link "ver registro #id" da aba de checks não é renderizado.
   */
  dsAttendanceContainerId?: number | null
}

interface DoctorStation extends BaseEntity, CoverageFields {
  doctorConfirmed: boolean
  requestAntecipe: boolean
  antecipeWasPaid: boolean
  date: string
  startHour: string
  endHour: string
  timezone: string
  insertDoctorDate: string | null
  ourTeam: boolean
  type: string
  doctor?: Doctor
  doctorChecks?: DoctorCheck[]
  userSponsor?: UserInfo
  dsPaymentDoctorFormation?: any | null
  value: number | null
  inCash: boolean
  modifiedValue?: boolean
  exceptionSummaryStatus?: ExceptionSummaryStatus | null
  observations?: Observation[]
  doctorStationReminderStatus?: DoctorStationReminderStatus
  approvedExceptionId?: number
  isAlert?: boolean
  exceptions: any[]
  doctorStationReminderRejectedDate: string | null
  doctorStationReminderAcceptedDate: string | null
  package?: {
    id: number
    isActive: boolean
    createdAt: string
    updatedAt: string
  }
  coverageResponsible?: { id: number; name: string } | null
}

export enum DoctorStationReminderStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED'
}

export type Alert = {
  isAlert: boolean
  title?: string
  description?: string
  dateTime?: Date
}

/**
 * Resposta de criar/editar plantao. O backend (`stations.service.ts`) roda
 * `checkDoctorOverlap` DENTRO da transacao de escrita e anexa `overlapAlerts` ao retorno:
 * a vaga ja esta gravada quando o aviso chega. Por isso e um aviso a posteriori,
 * nao uma pre-validacao — ver `ScheduleOverlapAlert.vue`.
 */
export interface StationMutationResponse {
  id?: number
  overlapAlerts?: Alert[]
  [key: string]: unknown
}

export interface ShiftDetails {
  /** Aditivo (acceptance-terms, backend #3572): convite pendente do plantão (D37). */
  allocationInvite?: StationAllocationInvite | null
  /** Aditivo (acceptance-terms, backend #3572): termo aceito da alocação atual (D19). */
  acceptanceTerm?: StationAcceptanceTerm | null
  id: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  date: string
  weekDay: number
  dayPosition: string | null
  startHour: string
  endHour: string
  timezone: string
  doctorStation: DoctorStation
  companyUserSponsor?: SponsorInfo | null
  schedule: Schedule
  position: Position
  shift: any | null
  observations?: Observation[]
  inCash: boolean
  type: string
  value: number | null
  /**
   * Valor de referência do plantão ocupado sem identificação (informativo, fora dos
   * totais). Ausente em BE antigo / não servido no detalhe → o card cai em `value`.
   */
  referenceValue?: number | null
  ourTeam: boolean
  alerts: Array<Alert>
  stationWeekParity: 0 | 1
  doctorNpsScore?: {
    npsScore: number
    avgScore: number
    evaluations: number
    profile: string
  } | null
  /** Contrato A9: `GET /stations/:id` devolve setor e especialidade do plantão. */
  sector?: StationSectorRef | null
  specialty?: StationSpecialtyRef | null
  /**
   * F2 — campo ADITIVO de `GET /stations/:id`: a flag global
   * `DS_MANUAL_CHECK_BLOCK_ENABLED` já resolvida para este plantão. Opcional
   * porque todo backend anterior à F2 não a devolve; nesse caso a edição do
   * plantão cai no fallback por `registration-settings` (B16/B17 expõem).
   */
  manualCheckBlockEnabled?: boolean
  /**
   * M7 — campos ADITIVOS de `GET /stations/:id` que dizem QUEM decidiu a exigência de
   * check no app deste plantão: a política de registro de plantão vigente na data
   * (`POLICY`), as flags legadas da escala enquanto ela não tem política (`SCHEDULE`)
   * ou ninguém (`NONE`).
   *
   * Todos OPCIONAIS de propósito: o backend está adicionando em paralelo e, enquanto
   * não vierem, a linha "Check no app" simplesmente não é renderizada
   * (`buildStationCheckLine` devolve `null`) — o Detalhes do Plantão degrada para o que
   * já existe hoje, sem quebrar e sem inventar um "exigido" sem dono.
   */
  requiredCheckSource?: RequiredCheckSource | null
  /** A política que decidiu, quando `requiredCheckSource === 'POLICY'`. */
  requiredCheckPolicy?: RequiredCheckPolicyRef | null
  /** A exigência já resolvida para ESTE plantão (público interno vs. externo incluído). */
  requiredCheck?: boolean | null
  /** O método de check exigido, quando há exigência. */
  requiredCheckMethod?: CheckMethod | null
}

export interface StationFormSchedule {
  stationId: number
  doctorStationId: number
  position: any | null
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  value: number | null
  isOpen: boolean
  doctorId: number | null
  packageId: number | null
  hasDoctorConflict: boolean
  coverageStatus?: CoverageStatus
  coverageResponsibleName?: string | null
  blockedReason?: CoverageBlockedReason | null
  /** Adições ao contrato (ver seção final do plano) — todas opcionais. */
  doctorName?: string | null
  pendingActivityRequests?: number
  hasPostStationEvaluation?: boolean
  pendingHandoverInvites?: number
}

export type ListStationsFromScheduleResponse = {
  stations: StationFormSchedule[]
  count: number
  countOpen: number
  countClosed: number
  countConflict: number
  stationWeekParity: number
  countUnregistered?: number
}

export interface SponsorInfo {
  id: number
  registration?: string | null
}

export type CalendarSummaryDay = { date: string; total: number; filled: number; open: number }

export type CalendarSummarySchedule = {
  scheduleId: number
  scheduleName: string
  healthUnitName: string
  days: { date: string; total: number; open: number }[]
}

/** `GET /stations/calendar-summary` (spec §4.5). `date` em `yyyy-MM-dd`. */
export type CalendarSummaryResponse = {
  days: CalendarSummaryDay[]
  schedules: CalendarSummarySchedule[]
  summary: {
    total: number
    filled: number
    open: number
    /** Ocupados sem identificação (já dentro de `filled`). Opcional: API antiga não manda. */
    unidentified?: number
    /** 0–100 (BE: 100 quando `total = 0`). */
    coveragePct: number
    /** Datas `yyyy-MM-dd` críticas segundo o BE; o web só recalcula a partir de `days` como fallback. */
    criticalDays: string[]
  }
}

/**
 * `GET /stations/matrix-summary` (Onda 2.1, spec §3). Cópia fiel de
 * `matrix-summary.types.ts` do BE (sem o `MatrixSummaryRow` interno do repositório).
 */
export type MatrixMetric = 'stations' | 'hours'
export type MatrixColumnSource = 'official' | 'company' | 'none'

/** Série diária de uma célula; cada array está alinhado a `MatrixSummaryResponse.days`. */
export interface MatrixDaily {
  total: number[]
  open: number[]
  awaiting: number[]
  hours: number[]
  openHours: number[]
}

export interface MatrixCell {
  total: number
  filled: number
  open: number
  awaiting: number
  /** Ocupados sem identificação (já dentro de `filled`). Opcional: BE antigo não manda. */
  unidentified?: number
  /** Horas com 1 casa decimal. */
  hours: number
  openHours: number
  awaitingHours: number
  /** 0–100 com 1 casa, na métrica pedida; `null` = célula sem plantão (nunca 100%). */
  coveragePct: number | null
  criticalDays: number
  daysWithStations: number
  daily: MatrixDaily
}

export interface MatrixColumn {
  /** `'official:<id>' | 'company:<id>' | 'none'` */
  key: string
  source: MatrixColumnSource
  id: number | null
  name: string
}

export interface MatrixSectorRow {
  sectorId: number | null
  sectorName: string | null
  archived: boolean
  /** Só as colunas com plantão, por `column.key`. */
  cells: Record<string, MatrixCell>
  total: MatrixCell
}

export interface MatrixUnitRow {
  healthUnitId: number
  healthUnitName: string
  /** Por `column.key`. */
  subtotal: Record<string, MatrixCell>
  total: MatrixCell
  sectors: MatrixSectorRow[]
}

export type MatrixPattern = 'chronic' | 'recurrent' | 'punctual' | 'none'

export interface MatrixTopItem {
  healthUnitId: number
  healthUnitName: string
  sectorId: number | null
  sectorName: string | null
  columnKey: string
  specialtyName: string
  source: MatrixColumnSource
  impact: number
  pattern: MatrixPattern
  cell: MatrixCell
}

export interface MatrixSummary {
  total: number
  filled: number
  open: number
  awaiting: number
  /** Ocupados sem identificação (já dentro de `filled`). Opcional: BE antigo não manda. */
  unidentified?: number
  hours: number
  openHours: number
  awaitingHours: number
  /** Na métrica pedida; `null` quando o denominador é 0. */
  coveragePct: number | null
  /** Células com plantão. */
  combinations: number
  /** Células com `coveragePct < 85`. */
  criticalCombinations: number
}

export interface MatrixSummaryResponse {
  metric: MatrixMetric
  /** `yyyy-MM-dd` do recorte. */
  days: string[]
  /** Ordem de exibição. */
  columns: MatrixColumn[]
  units: MatrixUnitRow[]
  /** Por `column.key`. */
  columnTotals: Record<string, MatrixCell>
  total: MatrixCell
  summary: MatrixSummary
  /** Até 5. */
  top: MatrixTopItem[]
}

export type BulkSectorSpecialtyFieldCounts = { filled: number; overwritten: number }

export type BulkSectorSpecialtyResponse = {
  updated: number
  overwritten: number
  filled: number
  /** Aditivo do BE (mockup H7): contadores por campo. */
  byField?: { sector: BulkSectorSpecialtyFieldCounts; specialty: BulkSectorSpecialtyFieldCounts }
}

/**
 * Resposta de `PATCH /stations/bulk` e `/bulk/all`: quantos plantões perderam o setor
 * (mudança de unidade, Q30) e a especialidade da empresa (mudança de empresa, A9).
 */
export type PatchStationsResponse = { sectorCleared?: number; specialtyCleared?: number }

/** Resposta de `PATCH /stations/bulk/move`: quantos moveram e o que o BE limpou (Q30/A9). */
export type MoveStationsResponse = {
  moved: number
  sectorCleared: number
  specialtyCleared: number
}
